import React from 'react';
import { Modal } from '@spotify-internal/anchor-ds';
import { ButtonTertiary } from '@spotify-internal/encore-web';
import { DropdownButton, MenuItem } from 'react-bootstrap';
import { LoadingButton } from 'client/shared/Spinner/LoadingButton';
import { css } from 'emotion';
import { RecordingNotSupported } from 'screens/VoiceMessageCreationModalScreen/components/RecordingNotSupported';
import Box from 'shared/Box';
import Heading from 'shared/Heading';
import Icon from 'shared/Icon';
import { If } from 'shared/If';
import Image from 'shared/Image';
import Input from 'shared/Input';
import { Linkable, LinkText } from 'shared/Link';
import { Mask } from 'shared/Mask';
import Recordable from 'shared/Recordable';
import Text from 'shared/Text';
import { paths } from 'client/shared/Paths';
import { useT } from 'client/i18n/hooks/useT';
// tslint:disable-next-line:no-var-requires
import {
  // @ts-ignore
  appStoreLink,
  // @ts-ignore
  isAndroidChrome,
  // @ts-ignore
  isIOS,
  // @ts-ignore
  playStoreLink,
} from 'helpers/serverRenderingUtils';
// @ts-ignore - ignore types error, won't fix since sass/css modules are deprecated
import styles from './CallJoinContent.sass';

export type RecordableProps = {
  isReadyForRecording: boolean;
  availableMicrophoneLabels: string[];
  currentMicrophoneLabel: string;
  isMicrophoneBlocked: boolean;
  isFetchingMicrophones: boolean;
  isRecordingSupported: boolean;
  selectMicrophoneWithLabel: any;
  startRecording: any;
  stopRecording: any;
  isRecording: any;
  durationInMs: any;
  reFetchMicrophones: any;
  getMicrophoneWithLabel: any;
};

interface CallJoinContentProps {
  isUserLoggedIn: boolean;
  podcastName: string;
  podcastCreatorUserName: string;
  loggedInUserName: string;
  notLoggedInUserName: string;
  email: string;
  onSubmit: any;
  onChangeDisplayName: any;
  onChangeEmail: any;
  onChangeMicrophone: any;
  coverartImageUrl: string;
  isFetching: boolean;
  onCloseErrorModal: any;
  isErrorModalOpen: boolean;
  onReadyForRecording: any;
}

const CallJoinContent = ({
  isUserLoggedIn,
  podcastCreatorUserName,
  loggedInUserName,
  notLoggedInUserName,
  podcastName,
  onSubmit,
  onChangeDisplayName,
  onChangeMicrophone,
  coverartImageUrl,
  onChangeEmail,
  email,
  isFetching,
  onCloseErrorModal,
  isErrorModalOpen,
  onReadyForRecording,
}: CallJoinContentProps) => {
  const t = useT();
  return (
    <Box
      display="flex"
      width="100%"
      justifyContent="center"
      className={styles.wrapper}
      smPaddingTop={24}
      mdPaddingTop={33}
      lgPaddingTop={33}
      smPaddingBottom={24}
      mdPaddingBottom={33}
      lgPaddingBottom={33}
      color="white"
    >
      <form
        onSubmit={evt => {
          evt.preventDefault();
          evt.stopPropagation();
          onSubmit();
        }}
      >
        <Recordable
          // @ts-ignore
          onReadyForRecording={microphone => {
            onReadyForRecording(microphone);
          }}
        >
          {({
            isReadyForRecording,
            availableMicrophoneLabels,
            currentMicrophoneLabel,
            selectMicrophoneWithLabel,
            isMicrophoneBlocked,
            isFetchingMicrophones,
            isRecordingSupported,
            reFetchMicrophones,
            getMicrophoneWithLabel,
          }: RecordableProps) => (
            <Box width="100%" color="white" marginBottom={15}>
              {isErrorModalOpen ? (
                <Modal
                  aria-label={t(
                    'CallJoin.CallJoinContent.ConnectionIssuesModalAriaLabel',
                    'Connectivity issues',
                    'Call with friends feature, connectivity issues modal screenreader information'
                  )}
                  isShowing={isErrorModalOpen}
                  onClose={onCloseErrorModal}
                  isShowingCloseButton
                >
                  <Modal.Image
                    as="div"
                    className={css`
                      max-width: 82px;
                      margin: auto;
                    `}
                  >
                    <Icon type="AnchorAppIcon" />
                  </Modal.Image>
                  <Modal.Body>
                    <p
                      dangerouslySetInnerHTML={{
                        __html: t(
                          'CallJoin.CallJoinContent.ConnectionIssuesModal.Message',
                          "We're having trouble connecting to this browser. Please try another browser or download the free <strong>Anchor mobile app</strong> to join this call.",
                          'Call with friends feature, connectivity issues modal message'
                        ),
                      }}
                    />
                  </Modal.Body>
                  {(isIOS() || isAndroidChrome()) && (
                    <Modal.Footer>
                      <Linkable
                        to={
                          isIOS()
                            ? appStoreLink()
                            : isAndroidChrome()
                            ? playStoreLink()
                            : ''
                        }
                      >
                        <div
                          className={css`
                            width: 177px;
                          `}
                        >
                          <Icon
                            type={
                              isIOS()
                                ? 'AppleAppStoreBadgeIcon'
                                : isAndroidChrome
                                ? 'GoogleAppStoreBadgeIcon'
                                : 'none'
                            }
                          />
                        </div>
                      </Linkable>
                    </Modal.Footer>
                  )}
                </Modal>
              ) : (
                isMicrophoneBlocked && (
                  <Modal
                    aria-labelledby="microphone-access-title"
                    isShowing={isMicrophoneBlocked}
                  >
                    <Modal.Title id="microphone-access-title">
                      {t(
                        'CallJoin.CallJoinContent.MicrophoneAccessRequiredModal.Title',
                        'Microphone access required',
                        'Call with friends feature, Microphone access required modal title'
                      )}
                    </Modal.Title>

                    <Modal.Body>
                      {t(
                        'CallJoin.CallJoinContent.MicrophoneAccessRequiredModal.Message',
                        "We've detected that Anchor does not have permissions to record from this browser. To fix this, please allow anchor.fm to access your microphone. (You can find this in your browser settings.)",
                        'Call with friends feature, Microphone access required modal message'
                      )}
                    </Modal.Body>
                    <Modal.Footer>
                      <ButtonTertiary
                        as="a"
                        href={paths.getHelpPage('newRequest')}
                      >
                        {t(
                          'CallJoin.CallJoinContent.MicrophoneAccessRequiredModal.ContactSupportButtonLabel',
                          'Still not working? Contact Anchor support',
                          'Call with friends feature, Microphone access required modal contact support button text'
                        )}
                      </ButtonTertiary>
                    </Modal.Footer>
                  </Modal>
                )
              )}

              <Modal
                aria-labelledby="recording-not-supported"
                isShowing={!isRecordingSupported}
              >
                <RecordingNotSupported
                  link={window.location.href}
                  headingText={t(
                    'CallJoin.CallJoinContent.RecordingNotSupportedModal.Title',
                    'Open in Safari to join this recording',
                    'Call with friends feature, recording not supported modal title'
                  )}
                  bodyText={t(
                    'CallJoin.CallJoinContent.RecordingNotSupportedModal.Message',
                    "Unfortunately, recording isn't supported from within this app. To join recording, follow these quick steps:",
                    'Call with friends feature, recording not supported modal message'
                  )}
                  onDidPressCopyLink={() => {}}
                  onComponentDidMount={() => {}}
                  labelledById="recording-not-supported"
                />
              </Modal>

              <Box
                display="flex"
                justifyContent="center"
                smMarginBottom={7}
                mdMarginBottom={7}
                lgMarginBottom={16}
                paddingLeft={36}
                paddingRight={36}
              >
                <Box maxWidth={913} width="100%">
                  <Heading
                    sizeAtSmScreen="sm"
                    sizeAtMdScreen="md"
                    sizeAtLgScreen="md"
                    isBold={true}
                    align="center"
                    color="#292F36"
                  >
                    {podcastName}
                  </Heading>
                </Box>
              </Box>
              <Box
                display="flex"
                justifyContent="center"
                smMarginBottom={14}
                mdMarginBottom={14}
                lgMarginBottom={20}
                paddingLeft={36}
                paddingRight={36}
              >
                <Box maxWidth={696} width="100%">
                  <Text
                    sizeAtSmScreen="lg"
                    sizeAtMdScreen="lg"
                    sizeAtLgScreen="xl"
                    color="#7F8287"
                    align="center"
                  >
                    {t(
                      'CallJoin.CallJoinContent.JoinCallInvitationMessage',
                      "You've been invited to join a recording for this podcast by {podcastCreator}",
                      'Call with friends feature, join call invitation message',
                      {
                        podcastCreator: (
                          <Text
                            sizeAtSmScreen="lg"
                            sizeAtMdScreen="lg"
                            sizeAtLgScreen="xl"
                            color="#7F8287"
                            align="center"
                            isBold={true}
                            isInline={true}
                            key={`invitation-for-creator-${
                              podcastCreatorUserName || ''
                            }`}
                          >
                            {podcastCreatorUserName}
                          </Text>
                        ),
                      }
                    )}
                  </Text>
                </Box>
              </Box>
              <Box
                display="flex"
                justifyContent="center"
                isHidingAtSmScreen={true}
                isHidingAtMdScreen={true}
              >
                <Box
                  smHeight={72}
                  smWidth={72}
                  mdHeight={100}
                  mdWidth={100}
                  lgHeight={100}
                  lgWidth={100}
                  marginBottom={25}
                  marginLeft={36}
                  marginRight={36}
                >
                  <Mask
                    isFullWidth={true}
                    isFullHeight={true}
                    shape="rounded"
                    boxShadow="0 2px 6px 0 rgba(0,0,0,0.4)"
                  >
                    <If
                      condition={Boolean(coverartImageUrl)}
                      ifRender={() => (
                        <Image
                          smHeight={72}
                          smWidth={72}
                          mdHeight={100}
                          mdWidth={100}
                          lgHeight={100}
                          lgWidth={100}
                          imageUrl={coverartImageUrl}
                          retinaImageUrl={coverartImageUrl}
                        />
                      )}
                      elseRender={() => (
                        <Box
                          width="100%"
                          height="100%"
                          color="#DEDFE0"
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                        >
                          <Box width={55}>
                            <Icon type="anchor_logo" fillColor="white" />
                          </Box>
                        </Box>
                      )}
                    />
                  </Mask>
                </Box>
              </Box>
              <If
                condition={!isUserLoggedIn}
                ifRender={() => (
                  <Box>
                    <Box
                      display="flex"
                      justifyContent="center"
                      marginBottom={16}
                      paddingLeft={36}
                      paddingRight={36}
                    >
                      <Box maxWidth={398} width="100%">
                        <Box>
                          <Box marginBottom={8}>
                            <Text size="lg" color="#292F36">
                              {t(
                                'CallJoin.CallJoinContent.NameInputFieldLabel',
                                'Your name',
                                'Call with friends feature, join call name input field label'
                              )}
                            </Text>
                          </Box>
                          <Box
                            borderColor="#DEDFE0"
                            borderWidth={2}
                            shape="rounded"
                          >
                            <Mask shape="rounded">
                              <Input
                                shape="rounded"
                                onChange={(value: string) => {
                                  onChangeDisplayName(value);
                                }}
                                fontSize={16}
                                value={notLoggedInUserName}
                                paddingLeft={0}
                                paddingRight={0}
                              />
                            </Mask>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      display="flex"
                      justifyContent="center"
                      marginBottom={16}
                      paddingLeft={36}
                      paddingRight={36}
                    >
                      <Box maxWidth={398} width="100%">
                        <Box>
                          <Box marginBottom={8}>
                            <Text size="lg" color="#292F36">
                              {t(
                                'CallJoin.CallJoinContent.EmailInputFieldLabel',
                                'Your email (optional)',
                                'Call with friends feature, join call email input field label'
                              )}
                            </Text>
                          </Box>
                          <Box
                            borderColor="#DEDFE0"
                            borderWidth={2}
                            shape="rounded"
                          >
                            <Mask shape="rounded">
                              <Input
                                fontSize={16}
                                type="email"
                                shape="rounded"
                                onChange={(value: string) => {
                                  onChangeEmail(value);
                                }}
                                value={email}
                                paddingLeft={0}
                                paddingRight={0}
                              />
                            </Mask>
                          </Box>
                          <Box marginTop={8}>
                            <Text size="sm" color="#7F8287">
                              {t(
                                'CallJoin.CallJoinContent.EmailInputFieldReasonMessage',
                                "We'll notify you if this gets published in an episode!",
                                'Call with friends feature, join call email input field reason message'
                              )}
                            </Text>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}
              />
              <Box
                marginTop={25}
                marginBottom={25}
                paddingLeft={36}
                paddingRight={36}
              >
                {availableMicrophoneLabels.length ? (
                  <Box display="flex" justifyContent="center">
                    <Box maxWidth={398} width="100%">
                      <div>
                        <Box marginBottom={8}>
                          <Text size="lg" color="#292F36">
                            {t(
                              'CallJoin.CallJoinContent.ChooseMicrophoneLabel',
                              'Choose microphone',
                              'Call with friends feature, join call choose microphone label'
                            )}
                          </Text>
                        </Box>
                        <div className={styles.control}>
                          <DropdownButton
                            onClick={() => {
                              reFetchMicrophones();
                            }}
                            onSelect={(microphoneLabel: any) => {
                              selectMicrophoneWithLabel(microphoneLabel);
                              getMicrophoneWithLabel(microphoneLabel).then(
                                (microphone: any) => {
                                  onChangeMicrophone(microphone.deviceId);
                                }
                              );
                            }}
                            title={
                              isFetchingMicrophones
                                ? t(
                                    'CallJoin.CallJoinContent.LoadingMicrophone',
                                    'Loading...',
                                    'Call with friends feature, loading microphone'
                                  )
                                : currentMicrophoneLabel || ''
                            }
                            id="Input Dropdown"
                          >
                            {availableMicrophoneLabels.map(
                              (microphoneLabel: any) => (
                                <MenuItem
                                  eventKey={microphoneLabel}
                                  key={microphoneLabel}
                                >
                                  {microphoneLabel}
                                </MenuItem>
                              )
                            )}
                          </DropdownButton>
                        </div>
                      </div>
                    </Box>
                  </Box>
                ) : null}
              </Box>
              <Box>
                <If
                  condition={isUserLoggedIn}
                  ifRender={() => (
                    <Box
                      display="flex"
                      justifyContent="center"
                      paddingLeft={36}
                      paddingRight={36}
                    >
                      <Box maxWidth={300} width="100%">
                        <Box>
                          <Text color="#292F36" align="center" size="lg">
                            {t(
                              'CallJoin.CallJoinContent.JoiningUserMessage',
                              "You're joining as {loggedInUserName}",
                              'Call with friends feature, user joining call message',
                              { loggedInUserName }
                            )}
                          </Text>
                        </Box>
                      </Box>
                    </Box>
                  )}
                />
              </Box>
              <Box
                display="flex"
                justifyContent="center"
                marginBottom={15}
                marginTop={18}
                paddingLeft={36}
                paddingRight={36}
              >
                <Box maxWidth={300} width="100%">
                  <LoadingButton
                    isLoading={isFetchingMicrophones || isFetching}
                    isDisabled={!notLoggedInUserName || !isReadyForRecording}
                    color="red"
                    type="submit"
                    className={css`
                      width: 100%;
                    `}
                  >
                    {t(
                      'CallJoin.CallJoinContent.JoinRecordingButtonLabel',
                      'Join Recording',
                      'Call with friends feature, join recording button text'
                    )}
                  </LoadingButton>
                </Box>
              </Box>
              <Box display="flex" justifyContent="center">
                <Box width="100%" maxWidth={237}>
                  <Text color="#7F8287" align="center">
                    {t(
                      'CallJoin.CallJoinContent.JoinRecordingConditionsMessage',
                      'By continuing, you agree to our {termsOfService} and {privacyPolicy}.',
                      'Call with friends feature, join recording conditions message',
                      {
                        termsOfService: (
                          <LinkText
                            key="terms-of-service-link"
                            target="_blank"
                            onClick={() => {}}
                            isInline={true}
                            to={paths.legal.tos}
                            color="gray"
                          >
                            {t(
                              'Link.TermsOfService',
                              'Terms of Service',
                              'Terms of Service link text'
                            )}
                          </LinkText>
                        ),
                        privacyPolicy: (
                          <LinkText
                            key="privacy-policy-link"
                            target="_blank"
                            onClick={() => {}}
                            isInline={true}
                            color="gray"
                            to={paths.legal.privacy}
                          >
                            {t(
                              'Link.PrivacyPolicy',
                              'Privacy Policy',
                              'Privacy Policy link text'
                            )}
                          </LinkText>
                        ),
                      }
                    )}
                  </Text>
                </Box>
              </Box>
            </Box>
          )}
        </Recordable>
      </form>
    </Box>
  );
};

export { CallJoinContent, CallJoinContentProps };
